/*
 * Dehde – cart behaviour (bible "carrello", 10x). Loaded by snippets/cart-drawer.liquid (every page) and by
 * sections/main-cart.liquid (/cart); every definition is guarded, so loading it twice is harmless.
 *
 *   <dehde-cart-invoice>  "Mi serve la fattura": inline validation + save as cart attributes (/cart/update.js)
 *   checkout gate         stops "Vai al checkout" while the invoice box is ticked and a field is invalid, and waits
 *                         for a pending attribute save before letting the checkout start
 *   <dehde-cart-bar>      phone: subtotal + checkout pinned at the bottom while the real button is off screen
 *   quote popup           opens the shop's ONE quote popup (snippets/dehde-quote-modal, panel <dehde-quote>
 *                         source 'cart', 2 steps) and reloads its lines from /cart.js on every open;
 *                         "Richiedi un preventivo" in the cart drawer → the same popup (on /cart in place,
 *                         elsewhere via /cart#preventivo)
 *
 * The theme re-renders the cart markup on every add / quantity change / remove (assets/global.js,
 * [data-line-items-container]); custom elements re-bind themselves in connectedCallback, so nothing here keeps
 * references to nodes that the theme may replace.
 */
(() => {
  if (window.DehdeCart) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /* ------------------------------------------------------------------ invoice */

  // attribute names written on the cart (and so on the order). Keep in sync with snippets/dehde-cart-invoice.liquid
  const INV_KEYS = { rs: 'Ragione sociale', piva: 'Partita IVA', cf: 'Codice fiscale', sdi: 'Codice SDI o PEC' };

  const normalize = {
    rs: (v) => v.trim().replace(/\s+/g, ' '),
    piva: (v) => v.replace(/\s+/g, '').replace(/^IT/i, ''),
    cf: (v) => v.replace(/\s+/g, '').toUpperCase(),
    sdi: (v) => {
      const t = v.trim();
      return t.includes('@') ? t : t.replace(/\s+/g, '').toUpperCase();
    }
  };

  const isValid = {
    rs: (v) => v.length > 0,
    piva: (v) => /^\d{11}$/.test(v),
    cf: (v) => v === '' || /^[A-Z0-9]{16}$/.test(v) || /^\d{11}$/.test(v),
    sdi: (v) => /^[A-Z0-9]{7}$/.test(v) || EMAIL_RE.test(v)
  };

  // one save queue for every instance (page + drawer write the same attributes)
  let saveChain = Promise.resolve();
  let pendingSaves = 0;

  function saveAttributes(attributes) {
    pendingSaves += 1;
    saveChain = saveChain
      .then(() => fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // no `sections` on purpose: nothing is re-rendered while the user types
        body: JSON.stringify({ attributes })
      }))
      .catch((e) => console.error('Dehde invoice save failed', e))
      .finally(() => { pendingSaves -= 1; });
    return saveChain;
  }

  function debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  }

  if (!customElements.get('dehde-cart-invoice')) {
    customElements.define('dehde-cart-invoice', class DehdeCartInvoice extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-inv-on]');
        this.fieldsBox = this.querySelector('[data-inv-fields]');
        this.savedBox = this.querySelector('[data-inv-saved]');
        this.inputs = {};
        Object.keys(INV_KEYS).forEach((k) => { this.inputs[k] = this.querySelector(`[data-inv-f="${k}"]`); });
        this.touched = {};
        this.queueSave = debounce(() => this.save(), 500);

        this.onToggle = this.onToggle.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onSync = this.onSync.bind(this);
        this.onEdit = this.onEdit.bind(this);

        this.toggle.addEventListener('change', this.onToggle);
        Object.values(this.inputs).forEach((input) => {
          input.addEventListener('input', this.onInput);
          input.addEventListener('blur', this.onBlur);
        });
        const edit = this.querySelector('[data-inv-edit]');
        if (edit) edit.addEventListener('click', this.onEdit);
        document.addEventListener('dehde:invoice', this.onSync);
      }

      disconnectedCallback() {
        document.removeEventListener('dehde:invoice', this.onSync);
      }

      isOn() { return this.toggle.checked; }

      values() {
        const out = {};
        Object.keys(this.inputs).forEach((k) => { out[k] = normalize[k](this.inputs[k].value); });
        return out;
      }

      setState(state) {
        this.dataset.state = state;
        this.fieldsBox.hidden = state !== 'open';
        if (this.savedBox) this.savedBox.hidden = state !== 'saved';
        const on = state !== 'off';
        Object.values(this.inputs).forEach((input) => { input.disabled = !on; });
        // page form: the hidden "clear" inputs are submitted only while the box is unticked
        this.querySelectorAll('[data-inv-clear]').forEach((i) => { i.disabled = on; });
      }

      onToggle() {
        if (this.isOn()) {
          this.setState('open');
          this.inputs.rs.focus();
        } else {
          this.setState('off');
          this.clearErrors();
        }
        this.save();
      }

      onEdit() {
        this.setState('open');
        this.inputs.rs.focus();
      }

      onInput(event) {
        const key = event.target.dataset.invF;
        // hide an error as soon as the value becomes valid; new errors wait for blur
        if (this.touched[key]) this.showError(key, !isValid[key](normalize[key](event.target.value)));
        this.queueSave();
      }

      onBlur(event) {
        const key = event.target.dataset.invF;
        const value = normalize[key](event.target.value);
        if (event.target.value !== value) event.target.value = value;
        this.touched[key] = true;
        // leaving the field FOR "Vai al checkout": do not show the message now — it would push the button down
        // between mousedown and mouseup and the click would miss it. The checkout gate shows every error instead.
        const toCheckout = event.relatedTarget && event.relatedTarget.matches('[data-dehde-checkout], [data-dehde-bar-checkout]');
        // an EMPTY field is not flagged on blur (only by the gate): leaving the invoice box for another control
        // must not shift the layout under the pointer
        if (!toCheckout && value !== '') this.showError(key, !isValid[key](value));
        this.save();
      }

      showError(key, show) {
        const error = this.querySelector(`[data-inv-e="${key}"]`);
        if (error) error.hidden = !show;
        this.inputs[key].setAttribute('aria-invalid', show ? 'true' : 'false');
      }

      clearErrors() {
        Object.keys(this.inputs).forEach((k) => this.showError(k, false));
      }

      // true when every field is valid; with show = true the errors are displayed
      validate(show) {
        const v = this.values();
        let ok = true;
        Object.keys(v).forEach((k) => {
          const valid = isValid[k](v[k]);
          if (!valid) ok = false;
          if (show) { this.touched[k] = true; this.showError(k, !valid); }
        });
        return ok;
      }

      focusFirstInvalid() {
        if (this.dataset.state !== 'open') this.setState('open');
        const v = this.values();
        const first = Object.keys(v).find((k) => !isValid[k](v[k]));
        if (first) this.inputs[first].focus();
      }

      save() {
        const on = this.isOn();
        const v = this.values();
        const attributes = { Fattura: on ? 'Sì' : '' };
        Object.keys(INV_KEYS).forEach((k) => { attributes[INV_KEYS[k]] = on ? v[k] : ''; });
        document.dispatchEvent(new CustomEvent('dehde:invoice', { detail: { source: this, on, values: v } }));
        return saveAttributes(attributes);
      }

      // keep the other instance (page ↔ drawer) in step without a re-render
      onSync(event) {
        const { source, on, values } = event.detail;
        if (source === this) return;
        this.toggle.checked = on;
        Object.keys(values).forEach((k) => {
          if (document.activeElement !== this.inputs[k]) this.inputs[k].value = values[k];
        });
        if (!on) this.setState('off');
        else if (this.dataset.state === 'off') this.setState('open');
        const t = this.querySelectorAll('[data-inv-t]');
        t.forEach((n) => { n.textContent = values[n.dataset.invT] || ''; });
      }
    });
  }

  /* ------------------------------------------------------------------ checkout gate */

  // every "Vai al checkout" of the theme is a submit button named "checkout" (cart page + drawer)
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== 'checkout') return;

    const inDrawer = !!form.closest('[data-modal="modal-cart-drawer"]');
    const invoice = inDrawer
      ? document.querySelector('[data-modal="modal-cart-drawer"] dehde-cart-invoice')
      : document.querySelector('dehde-cart-invoice[data-context="page"]');

    if (invoice && invoice.isOn() && !invoice.validate(true)) {
      event.preventDefault();
      // the drawer instance sits in the scrolling body, above the foot with the button
      invoice.focusFirstInvalid();
      return;
    }

    if (pendingSaves > 0) {
      event.preventDefault();
      submitter.disabled = true;
      saveChain.finally(() => {
        // form.submit() does not fire "submit" again and does not send the submitter: add it by hand
        const go = document.createElement('input');
        go.type = 'hidden';
        go.name = 'checkout';
        go.value = '';
        form.appendChild(go);
        HTMLFormElement.prototype.submit.call(form);
      });
    }
  }, true);

  /* ------------------------------------------------------------------ sticky bar (phone) */

  if (!customElements.get('dehde-cart-bar')) {
    customElements.define('dehde-cart-bar', class DehdeCartBar extends HTMLElement {
      connectedCallback() {
        this.target = document.querySelector(this.dataset.target);
        if (!this.target || !('IntersectionObserver' in window)) return;
        this.hidden = false;
        this.mq = window.matchMedia('(max-width: 991px)');
        this.observer = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-visible', this.mq.matches && !entry.isIntersecting);
        });
        this.observer.observe(this.target);
        const btn = this.querySelector('[data-dehde-bar-checkout]');
        // click the real button: the checkout gate and the native form post stay the only path
        if (btn) btn.addEventListener('click', () => this.target.click());
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    });
  }

  /* ------------------------------------------------------------------ quote popup */
  /*
   * The popup (sections/main-cart.liquid → snippets/dehde-quote-modal, modal "modal-dehde-cart-quote") holds the
   * shop's ONE quote panel <dehde-quote> (snippets/dehde-contract-quote-panel.liquid, source 'cart', mode 'popup';
   * behaviour in assets/dehde-contract-quote.js). Here only: open it, and hand it the LIVE cart every time it opens.
   * The panel's lines are seeded by Liquid at page load; after a remove / quantity change / add from a product
   * page the page itself is re-rendered but the popup is not, so without refreshCartLines() the request (and
   * contact[Prodotti] in the e-mail) would carry the cart as it was when the page loaded.
   */
  const QUOTE_MODAL = 'modal-dehde-cart-quote';

  function quoteModal() {
    return document.querySelector(`[data-modal="${QUOTE_MODAL}"]`);
  }

  // /cart.js line → the panel's item shape (same mapping as the panel's own ?da=carrello loader)
  function toQuoteItem(item) {
    return {
      title: item.product_title,
      vendor: item.vendor,
      variant: item.product_has_only_default_variant ? '' : item.variant_title,
      url: window.location.origin + item.url,
      image: item.image,
      price: item.final_price,
      qty: item.quantity
    };
  }

  // signature of the cart the panel was last filled from (key + quantity per line)
  let quoteCartSig = null;

  function refreshCartLines() {
    const modal = quoteModal();
    const panel = modal && modal.querySelector('dehde-quote');
    if (!panel || !window.customElements) return Promise.resolve();
    return customElements.whenDefined('dehde-quote').then(() => {
      // confirmation view (no form) or a panel that did not initialise: nothing to refresh
      if (!panel.form || panel.querySelector('[data-q-sent]') || typeof panel.addItems !== 'function') return null;
      return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (!cart || !Array.isArray(cart.items)) return;
          const sig = cart.items.map((i) => `${i.key}:${i.quantity}`).join('|');
          if (quoteCartSig === null) {
            // first open: the panel's Liquid seed was rendered from the cart at page load, whose signature
            // main-cart.liquid prints next to the popup (data-dehde-cart-sig) — compare with that
            quoteCartSig = document.querySelector('[data-dehde-cart-sig]')?.dataset.dehdeCartSig ?? '';
          }
          // same cart as last time: keep what the visitor already edited in the panel (quantities, removals)
          if (sig === quoteCartSig) return;
          quoteCartSig = sig;
          // setItems() REPLACES the lines and keeps the step (popup step 1 "La tua richiesta" = the lines with
          // their quantities). Origine stays "Carrello".
          panel.setItems(cart.items.map(toQuoteItem));
        });
    }).catch((e) => console.error('Dehde quote: cart refresh failed', e));
  }

  function openQuote() {
    const modal = quoteModal();
    if (!modal) return false;
    const drawer = document.querySelector('[data-modal="modal-cart-drawer"]');
    if (drawer && drawer.classList.contains('modal--active')) drawer.close();
    const opener = document.querySelector('[data-dehde-quote-open]');
    refreshCartLines();
    // same bookkeeping as the theme's <modal-component-toggler> (assets/global.js), so the close button and a
    // second click behave as if the page button had been used
    modal.open(opener);
    if (opener) opener.setAttribute('data-expanded', 'true');
    return true;
  }

  document.addEventListener('click', (event) => {
    // page button (Ilona card): the theme toggler opens the popup; refresh the lines on EVERY click.
    // (Do not test data-expanded here: the theme sets it to "true" during its own click listener, before this
    // document-level one runs. The button is covered by the overlay while the drawer is open, and the cart
    // signature makes a repeated refresh a no-op.)
    if (event.target.closest('[data-dehde-quote-open]')) {
      refreshCartLines();
      return;
    }
    // drawer link: on /cart open the quote in place, elsewhere follow the link to /cart#preventivo
    const link = event.target.closest('[data-dehde-quote-link]');
    if (link && quoteModal()) {
      event.preventDefault();
      openQuote();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    const modal = quoteModal();
    // after Shopify's POST reload the panel's success / error node carries data-dehde-quote-autoopen:
    // reopen the popup so the visitor sees the result (same pattern as the PDP quote popup, dehde-pdp.js)
    if (modal && modal.querySelector('[data-dehde-quote-autoopen]')) {
      requestAnimationFrame(() => {
        openQuote();
        const status = modal.querySelector('[data-dehde-quote-autoopen]');
        if (status) status.focus();
      });
      return;
    }
    if (window.location.hash === '#preventivo') openQuote();
  });

  window.DehdeCart = { openQuote };
})();
